import { ChangeDetectorRef, Component, Input, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { Subscription } from 'rxjs';
import { WorkItemListService } from 'common/workitems/workitem-list.service';
import { WorkItemListenerService } from 'common/workitems/workitem-listener.service';
import { DiscussionList } from 'common/workitems/lists/discussion-list';
import { isString } from 'lodash';
import { WorkItemStateHandler } from 'shared/services/workitem-state-handler/workitem-state-handler.service';
import { filter, first, mergeMap, takeUntil } from 'rxjs/operators';
import { DiscussionItem } from 'common/workitems/interfaces/discussion-item.interface';
import { ThemeServiceHelper } from 'common/theme/service.helper';
import { DiscussionsMessagesService } from 'shared/components/discussions/discussions-messages/discussions-messages.service';
import { DiscussionsMessagesTypes } from 'shared/components/discussions/discussion-properties';
import { WorkItemTypeValue } from 'common/workitems/constants/workitem-types';
import { KeyboardKeyCodes } from 'common/common/constants/keyboard-key-codes';
import { taskItemAnimation } from 'components/accordion/workitem-list/shared/animations/task-item.animation';

@Component({
  selector: 'app-work-item-discussion-list',
  templateUrl: './workitem-discussion-list.component.html',
  styleUrls: ['./workitem-discussion-list.component.scss'],
  animations: [taskItemAnimation],
  encapsulation: ViewEncapsulation.None
})
export class WorkItemDiscussionListComponent implements OnInit, OnDestroy {
  @Input()
  isHybrid: boolean;
  public discussionWorkItemList;
  private _activeDiscussionsSubscription;
  private _insertDiscussionMessagesSubscription: Subscription;
  private _insertSubscription: Subscription;
  private _updateSubscription: Subscription;
  private _deleteSubscription: Subscription;
  private _doneSubscription: Subscription;

  constructor(private workItemListenerService: WorkItemListenerService,
              private _changeDetector: ChangeDetectorRef,
              private _discussionList: DiscussionList,
              private _workItemStateHandler: WorkItemStateHandler,
              private _workItemListService: WorkItemListService,
              private _discussionMessagesService: DiscussionsMessagesService) {
    this.discussionWorkItemList = _discussionList.activeDiscussionWorkItemList;
    this._workItemListService.discussionWorkItemList = this.discussionWorkItemList;
  }

  ngOnInit() {
    this._initSubscriptions();
  }

  ngOnDestroy() {
    this._clearSubscriptions();
    this._discussionList.resetDiscussions();
  }

  public getActiveDiscussionsCount(): number {
    return this._discussionList.discussionWorkItemCount;
  }

  public getDiscussionAvatarClass(id): string {
    return ThemeServiceHelper.getCssClass(id);
  }

  public onDiscussionItemClicked(discussion) {
    this.selectWorkItem(discussion);
  }

  public onDiscussionItemKeydown(event, discussion) {
    if (event.which !== KeyboardKeyCodes.ENTER) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();

    this.selectWorkItem(discussion);
  }

  public getInitials(item): string {
    if (!item.OperatorName) {
      return null;
    }
    return this._createInitialsForAvatar(item.OperatorName);
  }

  public isFocused(workItemId: string): boolean {
    return this._workItemStateHandler.isFocused(workItemId);
  }

  public isOpened(workItem): boolean {
    return this._workItemStateHandler.isOpened(workItem.id);
  }

  public selectWorkItem(workitem: DiscussionItem) {
    this._workItemListService.selectAndAcceptDiscussionItem(workitem);
  }

  public getSubWorkItemIcon(workItem) {
    switch (workItem.WorkItemType) {
      case WorkItemTypeValue.CHAT:
        return 'chat-large';
      case WorkItemTypeValue.EMAIL:
        return 'mail';
      default:
        return '';
    }
  }

  public trackByItemId(index, discussion): string {
    return discussion.ItemID;
  }

  private _initSubscriptions(): void {
    this._clearSubscriptions();

    this._activeDiscussionsSubscription = this.workItemListenerService.observeActiveDiscussionsByLoginId();

    this._insertSubscription = this._activeDiscussionsSubscription.inserted()
      .subscribe(dynamicQueryEvent => this._itemInserted(dynamicQueryEvent));
    this._updateSubscription = this._activeDiscussionsSubscription.updated()
      .subscribe(dynamicQueryEvent => this._itemUpdated(dynamicQueryEvent));
    this._deleteSubscription = this._activeDiscussionsSubscription.deleted()
      .subscribe(dynamicQueryEvent => this._itemDeleted(dynamicQueryEvent));

    this._observeMessagesForWorkItem();
  }

  private _clearSubscriptions(): void {
    if (this._insertSubscription) {
      this._insertSubscription.unsubscribe();
      this._insertSubscription = null;
    }
    if (this._updateSubscription) {
      this._updateSubscription.unsubscribe();
      this._updateSubscription = null;
    }
    if (this._deleteSubscription) {
      this._deleteSubscription.unsubscribe();
      this._deleteSubscription = null;
    }
    if (this._doneSubscription) {
      this._doneSubscription.unsubscribe();
      this._doneSubscription = null;
    }
    if (this._activeDiscussionsSubscription) {
      this._activeDiscussionsSubscription = null;
    }
    if (this._insertDiscussionMessagesSubscription) {
      this._insertDiscussionMessagesSubscription.unsubscribe();
      this._insertDiscussionMessagesSubscription = null;
    }
  }

  private _itemInserted(item): void {
    this.discussionWorkItemList.push(item);
  }

  private _itemUpdated(item): void {
    const indexInList = this.discussionWorkItemList.findIndex(listItem => listItem.ItemID === item.ItemID);
    if (indexInList === -1) {
      this.discussionWorkItemList.push(item);
    } else {
      this.discussionWorkItemList[indexInList] = item;
    }
  }

  private _itemDeleted(item): void {
    const indexInList = this.discussionWorkItemList.findIndex(listItem => listItem.ItemID === item.ItemID);
    if (indexInList !== -1) {
      this.discussionWorkItemList.splice(indexInList, 1);
    }

    this._workItemListService.removeWorkItem(item);
    this._changeDetector.detectChanges();
  }

  private _observeMessagesForWorkItem(): void {
    this._insertDiscussionMessagesSubscription = this._activeDiscussionsSubscription.inserted().pipe(
      mergeMap(workItem => {
        return this.workItemListenerService.createObservableForWorkItemLastDiscussionMessages(workItem).pipe(
          takeUntil(this._observeDiscussionWorkItemRemoval(workItem))
        );
      })
    ).subscribe(workItemMessage => this._insertWorkItemMessage(workItemMessage));
  }

  private _insertWorkItemMessage(workItemMessage): void {
    this._buildLastMessageObject(workItemMessage);
  }

  private _buildLastMessageObject(workItemMessage) {
    const workitem = this._discussionList.getDiscussionItemById(workItemMessage.ItemID);
    if (workitem) {
      workitem.messages = workItemMessage.messages.map(message => {
        if (message.MessageType === DiscussionsMessagesTypes.SYSTEM) {
          message.text = this._discussionMessagesService.formatSystemMessage(message);
        } else {
          message.text = this._htmlToPlaintext(message.DiscussionMessage);
        }
        return message;
      });
    }
  }

  private _htmlToPlaintext(text): string {
    const strippedText = text && text.replace ? text.replace(/<[^>]+>/gm, '') : '';
    if (strippedText.length > 100) {
      return strippedText.substring(0, 100);
    }

    return strippedText;
  }

  private _observeDiscussionWorkItemRemoval(observedWorkItem) {
    return this._activeDiscussionsSubscription.deleted().pipe(
      filter((workItem: DiscussionItem) => workItem.ItemID === observedWorkItem.ItemID),
      first()
    );
  }

  private _createInitialsForAvatar(operatorName): string {
    if (isString(operatorName)) {
      const words = operatorName.toUpperCase().split(' ');
      let initial = '';

      words.forEach(value => {
        initial += value[0];
      });

      return initial;
    }

    return null;
  }

}
